import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './../components/ui/button';
import { toast } from 'sonner';
import {
  Loader2, Inbox, FileText, Sparkles, Building2, MapPin, Banknote,
  Factory, UserRound, CheckCircle2, X, Mail, ExternalLink, FileSignature, XCircle, Send, ChevronRight, Clock,
  BellRing, BellOff,
} from 'lucide-react';
import { STAGE_LABELS } from '../lib/stages';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './../components/ui/dialog';
import { CountdownBar, DeclineDialog, NdaDialog, NdaRequestDialog, TemplatesButton, BrokerStatsSection } from '../components/BrokerInboxExtras';
import { AddMailDialog } from '../components/AddMailDialog';

const fmtDt = (iso) => iso
  ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '';

const timeAgo = (iso) => {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'gerade eben';
  if (mins < 60) return `vor ${mins} Min`;
  const h = Math.round(mins / 60);
  return h < 24 ? `vor ${h} Std` : `vor ${Math.round(h / 24)} Tg`;
};

function AiChip({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-mono border border-border bg-secondary/50 text-foreground/80 rounded px-1.5 py-0.5" title={label}>
      <Icon className="w-3 h-3 text-sky-400" /> {value}
    </span>
  );
}

export default function DealInboxPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const highlightId = params.get('nda_remind');
  const hl = (id, base) => `${base} ${id === highlightId ? 'border-amber-500/70 ring-1 ring-amber-500/40' : ''}`;
  const [proposals, setProposals] = useState([]);
  const [processed, setProcessed] = useState([]);
  const [createdLive, setCreatedLive] = useState([]);
  const [createdPassed, setCreatedPassed] = useState([]);
  const [declinedList, setDeclinedList] = useState([]);
  const [passedOpen, setPassedOpen] = useState(false);
  const [scanStatus, setScanStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [declineFor, setDeclineFor] = useState(null);
  const [ndaFor, setNdaFor] = useState(null);
  const [ndaRequestFor, setNdaRequestFor] = useState(null);
  const [addMailOpen, setAddMailOpen] = useState(false);
  const [emailScan, setEmailScan] = useState(null);
  const [scanBusy, setScanBusy] = useState(false);
  const [dupFor, setDupFor] = useState(null);
  const [dupOf, setDupOf] = useState('');
  const [dupBusy, setDupBusy] = useState(false);

  const load = useCallback(() => {
    api.getDealProposals()
      .then(r => {
        setProposals(r.data.proposals || []); setProcessed(r.data.processed || []);
        setCreatedLive(r.data.created_live || []); setCreatedPassed(r.data.created_passed || []);
        setDeclinedList(r.data.declined || []);
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to load deal inbox'))
      .finally(() => setLoading(false));
    api.getDealScanStatus()
      .then(r => setScanStatus(r.data.mailboxes || []))
      .catch(() => {});
    api.getEmailPassScan()
      .then(r => setEmailScan(r.data.scan || null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (emailScan?.status !== 'running') return;
    const t = setInterval(() => {
      api.getEmailPassScan().then(r => {
        const s = r.data.scan;
        setEmailScan(s);
        if (s?.status === 'done') {
          toast.success(`E-Mail-Scan fertig — ${s.passed_found || 0} Absage${(s.passed_found || 0) === 1 ? '' : 'n'} erkannt (${s.checked} Deals geprüft)`);
          load();
        } else if (s?.status === 'failed') {
          toast.error('E-Mail-Scan fehlgeschlagen');
        }
      }).catch(() => {});
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailScan?.status]);

  const startEmailScan = async () => {
    setScanBusy(true);
    try {
      await api.startEmailPassScan();
      const r = await api.getEmailPassScan();
      setEmailScan(r.data.scan || { status: 'running' });
      toast.info('Scanne gesendete E-Mails auf Absagen…');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan konnte nicht gestartet werden');
    }
    setScanBusy(false);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const createDeal = async (p) => {
    setBusy(p.id);
    try {
      const r = await api.createDealFromProposal(p.id);
      if (r.data.merged_into_existing) {
        toast.success(`Als bestehender Deal „${r.data.name}" erkannt — ${r.data.attached_documents || 0} Anhang/Anhänge dort abgelegt, kein Duplikat erstellt`);
      } else {
        toast.success(`Deal "${r.data.name}" created in Under OLTA Review${r.data.teaser_attached ? ' — teaser attached, AI screening running' : ' — AI screening from email text'}`);
      }
      navigate(`/projects/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create deal');
      setBusy(null);
    }
  };

  const dismiss = async (p) => {
    setBusy(p.id);
    try { await api.dismissDealProposal(p.id); toast.success('Proposal dismissed'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setBusy(null);
  };

  const remindBroker = async (p) => {
    setBusy(p.id);
    try { await api.remindProposalNda(p.id); toast.success(`NDA-Erinnerung an ${p.from_name || p.from_email} gesendet`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    setBusy(null);
  };

  const dismissBrokerReminder = async (p) => {
    setBusy(p.id);
    try { await api.dismissProposalNdaReminder(p.id); toast.success('Keine NDA-Erinnerung für diesen Broker'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    setBusy(null);
  };

  const ndaRemindButtons = (p, testPrefix, cls) => (
    <span className="inline-flex items-center gap-1" data-testid={`${testPrefix}-nda-remind-due-${p.id}`}>
      <button onClick={() => remindBroker(p)} disabled={busy === p.id} data-testid={`${testPrefix}-nda-remind-btn-${p.id}`}
        title={`NDA vor ${p.nda_requested_days} Tagen angefordert, noch nichts erhalten — freundliche Erinnerung im selben Mail-Thread (genau einmal)`}
        className={`inline-flex items-center gap-1 border border-amber-500/50 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 rounded-sm transition-colors ${cls}`}>
        <BellRing className="w-3 h-3" /> Broker erinnern · NDA seit {p.nda_requested_days} Tg offen
      </button>
      <button onClick={() => dismissBrokerReminder(p)} disabled={busy === p.id} title="Nicht erinnern"
        data-testid={`${testPrefix}-nda-remind-dismiss-${p.id}`} className="text-muted-foreground hover:text-foreground p-0.5">
        <BellOff className="w-3 h-3" />
      </button>
    </span>
  );

  const markDuplicate = async () => {
    if (!dupFor) return;
    setDupBusy(true);
    try {
      await api.markProposalDuplicate(dupFor.id, dupOf);
      toast.success('Als Duplikat markiert — Deal wurde ohne Broker-Mail auf Passed gesetzt');
      setDupFor(null); setDupOf('');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    setDupBusy(false);
  };

  const respondedLabel = (p) => {
    if (p.nda_signed_at) return 'Beantwortet — NDA gegengezeichnet & zurückgesendet';
    if (p.nda_remarks_sent_at) return 'Beantwortet — NDA-Anmerkungen gesendet';
    if (p.nda_requested_at) return 'Beantwortet — NDA angefordert';
    return null;
  };

  const autoAckBadge = (p) => {
    if (p.auto_ack_sent_at) return { text: 'Auto-Antwort gesendet', cls: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' };
    if (p.auto_ack_due_at) {
      const t = new Date(p.auto_ack_due_at);
      if (t.getTime() > Date.now()) return { text: `Auto-Antwort ${t.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`, cls: 'border-sky-500/40 bg-sky-500/10 text-sky-300' };
      return { text: 'Auto-Antwort in Zustellung…', cls: 'border-sky-500/40 bg-sky-500/10 text-sky-300' };
    }
    return null;
  };

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="deal-inbox-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-foreground tracking-tight">Broker Deal Inbox</h1>
        <p className="text-muted-foreground text-sm mt-1">
          AI-detected deal proposals from incoming broker emails (all connected team mailboxes, scanned every 10 min).
          One click creates the deal, links the broker and starts the AI screening.
        </p>
        {scanStatus && scanStatus.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mt-2.5" data-testid="deal-inbox-scan-status">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Letzter Scan:</span>
            {scanStatus.map(m => (
              <span key={m.user_id} title={m.account}
                className="inline-flex items-center gap-1 text-[10px] font-mono border border-border bg-secondary/50 rounded px-1.5 py-0.5 text-foreground/80"
                data-testid={`scan-status-${m.user_id}`}>
                <Mail className="w-3 h-3 text-sky-400" />
                {m.name} · {m.last_run_at ? `${timeAgo(m.last_run_at)} · ${m.last_checked} Mails geprüft` : 'noch kein Scan'}
              </span>
            ))}
          </div>
        )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setAddMailOpen(true)} data-testid="add-mail-open-btn"
            className="border-sky-500/40 text-sky-300 hover:bg-sky-500/10 rounded-sm text-xs h-9">
            <Mail className="w-3.5 h-3.5 mr-1.5" /> E-Mail hinzufügen
          </Button>
          <TemplatesButton />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : proposals.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm py-14 text-center">
          <Inbox className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No new deal proposals — you'll get a push notification when the AI detects one.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {proposals.map((p, i) => (
            <div key={p.id} className={hl(p.id, 'bg-card border border-sky-600/30 rounded-sm p-4')} data-testid={`deal-proposal-${i}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Sparkles className="w-4 h-4 text-sky-400 shrink-0" />
                    <span className="font-heading text-base text-foreground" data-testid={`proposal-company-${i}`}>
                      {p.ai?.company_name || 'Unknown company (anonymous teaser)'}
                    </span>
                    <span className="text-[10px] font-mono border border-sky-600/40 bg-sky-600/10 text-sky-300 rounded px-1.5 py-0.5">
                      AI {Math.round((p.ai?.confidence || 0) * 100)}%
                    </span>
                    {p.has_pdf && (
                      <span className="text-[10px] font-mono border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 rounded px-1.5 py-0.5 flex items-center gap-1">
                        <FileText className="w-3 h-3" /> Teaser PDF
                      </span>
                    )}
                    {p.nda_attachment && (
                      <span className="text-[10px] font-mono border border-violet-500/40 bg-violet-500/10 text-violet-300 rounded px-1.5 py-0.5 flex items-center gap-1"
                        data-testid={`nda-attached-badge-${i}`}>
                        <FileSignature className="w-3 h-3" /> NDA dabei
                      </span>
                    )}
                    {(() => { const b = autoAckBadge(p); return b ? (
                      <span className={`text-[10px] font-mono border rounded px-1.5 py-0.5 flex items-center gap-1 ${b.cls}`}
                        data-testid={`auto-ack-badge-${i}`}>
                        <Send className="w-3 h-3" /> {b.text}
                      </span>
                    ) : null; })()}
                  </div>
                  <p className="text-sm text-foreground/85 mt-1.5">{p.ai?.summary}</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <AiChip icon={Factory} label="Industry" value={p.ai?.industry} />
                    <AiChip icon={MapPin} label="Location" value={p.ai?.location} />
                    <AiChip icon={Banknote} label="Revenue" value={p.ai?.revenue} />
                  </div>
                  <div className="flex items-center gap-2 mt-2.5 text-xs text-muted-foreground flex-wrap">
                    <UserRound className="w-3.5 h-3.5" />
                    <span className="text-foreground/80">{p.from_name || p.from_email}</span>
                    {p.contact_id && !p.contact_auto_created && (
                      <span className="text-[9px] font-mono border border-sky-500/40 bg-sky-500/10 text-sky-300 rounded px-1 py-0.5">Known broker</span>
                    )}
                    {p.contact_auto_created && (
                      <span className="text-[9px] font-mono border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 rounded px-1 py-0.5"
                        data-testid={`auto-broker-badge-${i}`}>Broker neu angelegt (KI)</span>
                    )}
                    <Mail className="w-3 h-3" /> {p.from_email}
                    <span>· {fmtDt(p.ts)}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1 truncate" title={p.subject}>Subject: {p.subject}</p>
                  {(p.attachments || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {p.attachments.map((a, j) => (
                        <span key={j} className="text-[9px] font-mono text-muted-foreground border border-border rounded px-1 py-0.5 flex items-center gap-1">
                          <FileText className="w-3 h-3" /> {a.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Button onClick={() => createDeal(p)} disabled={busy === p.id} data-testid={`proposal-create-btn-${i}`}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm text-xs h-9 px-4">
                    {busy === p.id ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                    Create Deal
                  </Button>
                  {p.nda_attachment ? (
                    <Button variant="outline" onClick={() => setNdaFor(p)} disabled={busy === p.id} data-testid={`proposal-nda-view-btn-${i}`}
                      className={`rounded-sm text-xs h-8 ${p.nda_signed_at ? 'border-emerald-500/40 text-emerald-300' : p.nda_sign_request ? 'border-sky-500/40 text-sky-300' : 'border-violet-500/40 text-violet-300 hover:bg-violet-500/10'}`}>
                      <FileSignature className="w-3.5 h-3.5 mr-1.5" /> {p.nda_signed_at ? 'NDA gezeichnet ✓' : p.nda_sign_request ? 'NDA wartet auf Freigabe' : 'NDA ansehen & zeichnen'}
                    </Button>
                  ) : (p.nda_prior_signed_at || p.ai?.nda_already_signed) ? (
                    <Button variant="outline" disabled data-testid={`proposal-nda-prior-${i}`}
                      className="border-emerald-500/40 text-emerald-300 rounded-sm text-xs h-8 opacity-90">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                      {p.nda_prior_signed_at
                        ? `NDA liegt vor ✓ (${new Date(p.nda_prior_signed_at).toLocaleDateString('de-DE')})`
                        : 'NDA bereits unterzeichnet ✓'}
                    </Button>
                  ) : p.nda_requested_at ? (
                    p.nda_reminder_due ? ndaRemindButtons(p, 'proposal', 'text-xs h-8 px-2.5') : (
                    <Button variant="outline" disabled data-testid={`proposal-nda-requested-${i}`}
                      className="border-emerald-500/40 text-emerald-300 rounded-sm text-xs h-8 opacity-80">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> NDA angefordert{p.nda_reminder?.sent_at ? ` · erinnert ${fmtDt(p.nda_reminder.sent_at).slice(0, 6)}` : ''}
                    </Button>)
                  ) : (
                    <Button variant="outline" onClick={() => setNdaRequestFor(p)} disabled={busy === p.id} data-testid={`proposal-nda-request-btn-${i}`}
                      className="border-violet-500/40 text-violet-300 hover:bg-violet-500/10 rounded-sm text-xs h-8">
                      <FileSignature className="w-3.5 h-3.5 mr-1.5" /> NDA anfordern
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setDeclineFor(p)} disabled={busy === p.id} data-testid={`proposal-decline-btn-${i}`}
                    className="border-red-500/40 text-red-300 hover:bg-red-500/10 rounded-sm text-xs h-8">
                    <XCircle className="w-3.5 h-3.5 mr-1.5" /> Absagen
                  </Button>
                  <Button variant="outline" onClick={() => dismiss(p)} disabled={busy === p.id} data-testid={`proposal-dismiss-btn-${i}`}
                    className="border-border text-muted-foreground hover:text-red-400 rounded-sm text-xs h-8">
                    <X className="w-3.5 h-3.5 mr-1.5" /> Dismiss
                  </Button>
                </div>
              </div>
              <CountdownBar ts={p.ts} respondedLabel={respondedLabel(p)} />
            </div>
          ))}
        </div>
      )}

      {createdLive.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Live brokered deals ({createdLive.length})</p>
            <button onClick={startEmailScan} disabled={scanBusy || emailScan?.status === 'running'}
              data-testid="email-pass-scan-btn"
              title="Scannt die gesendeten Mails an jeden Broker: Wurde der Deal schon per E-Mail abgesagt? Dann wird er automatisch auf Passed gesetzt (inkl. Grund)."
              className="ml-auto inline-flex items-center gap-1.5 text-[10px] border border-violet-500/40 text-violet-300 hover:bg-violet-500/10 disabled:opacity-50 rounded-sm px-2 py-1 transition-colors">
              {emailScan?.status === 'running'
                ? <><Loader2 className="w-3 h-3 animate-spin" /> Scanne E-Mails… {emailScan?.checked || 0} geprüft</>
                : <><Mail className="w-3 h-3" /> Auf E-Mail-Absagen prüfen</>}
            </button>
            {emailScan?.status === 'done' && (
              <span className="text-[9px] font-mono text-muted-foreground" data-testid="email-pass-scan-summary"
                title={`Zuletzt geprüft: ${fmtDt(emailScan.finished_at)}`}>
                zuletzt: {emailScan.checked} geprüft · {emailScan.passed_found || 0} Absage{(emailScan.passed_found || 0) === 1 ? '' : 'n'}
              </span>
            )}
          </div>
          <div className="space-y-1" data-testid="created-live-deals">
            {createdLive.map(p => (
              <div key={p.id} className={hl(p.id, 'flex items-center gap-2 px-3 py-1.5 border border-border/50 rounded-sm text-xs text-muted-foreground flex-wrap')}
                data-testid={`live-deal-row-${p.id}`}>
                <span className="text-[9px] font-mono border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 rounded px-1 py-0.5 uppercase">live</span>
                <span className="text-foreground/80">{p.project_name || p.ai?.company_name || p.subject}</span>
                <span className="hidden sm:inline">· {p.from_email}</span>
                <span className="text-[10px] text-sky-300/80">{STAGE_LABELS[p.project_stage] || p.project_stage}</span>
                {Number.isFinite(p.inactive_bdays) && (() => {
                  const d = p.inactive_bdays;
                  const cls = d > 5 ? 'border-red-500/40 bg-red-500/10 text-red-300' : d >= 3 ? 'border-amber-500/40 bg-amber-500/10 text-amber-300' : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300';
                  return (
                    <span className={`inline-flex items-center gap-1 text-[9px] font-mono border rounded px-1 py-0.5 ${cls}`}
                      title={`Letzte Aktivität am Deal: ${p.last_activity_at ? fmtDt(p.last_activity_at) : '—'} — Broker nie zu lange warten lassen`}
                      data-testid={`live-deal-timer-${p.id}`}>
                      <Clock className="w-3 h-3" />
                      {d === 0 ? 'heute aktiv' : `${d} Werktag${d === 1 ? '' : 'e'} ohne Aktivität`}
                    </span>
                  );
                })()}
                {(p.mail_count || 1) > 1 && (
                  <span className="text-[9px] font-mono border border-border bg-secondary/40 text-muted-foreground rounded px-1 py-0.5"
                    title={`${p.mail_count} Broker-Mails zu diesem Deal zusammengefasst`} data-testid={`live-mail-count-${p.id}`}>
                    {p.mail_count}× Mail
                  </span>
                )}
                {p.possible_duplicate_of && (
                  <span className="inline-flex items-center gap-1 text-[9px] font-mono border border-red-500/40 bg-red-500/10 text-red-300 rounded px-1 py-0.5"
                    title={`Gleicher Broker + gleicher Projektname wie „${p.possible_duplicate_of.name}“ — vermutlich doppelt angelegt. Über „Duplikat…“ zusammenführen.`}
                    data-testid={`live-possible-dup-${p.id}`}>
                    Mögliches Duplikat von {p.possible_duplicate_of.name}
                  </span>
                )}
                {p.pass_pending && (
                  <span className="text-[9px] font-mono border border-amber-500/40 bg-amber-500/10 text-amber-300 rounded px-1 py-0.5"
                    title="Pass wurde vorgeschlagen — wartet auf Bestätigung (Vier-Augen). Erst danach wandert der Deal in »Passed«."
                    data-testid={`live-pass-pending-${p.id}`}>
                    Pass vorgeschlagen
                  </span>
                )}
                {p.created_project_id && (
                  <div className="flex items-center gap-1.5 ml-auto flex-wrap">
                    {(() => {
                      const st = p.nda_stage?.key || 'none';
                      const openNda = () => p.nda_attachment ? setNdaFor(p) : navigate(`/projects/${p.created_project_id}`);
                      const chip = (cls, text, testid, title) => (
                        <span className={`text-[9px] font-mono border rounded px-1 py-0.5 ${cls}`} data-testid={testid} title={title || ''}>{text}</span>
                      );
                      switch (st) {
                        case 'sign_pending':
                          return chip('border-sky-500/40 bg-sky-500/10 text-sky-300', 'NDA wartet auf Freigabe', `live-nda-pending-${p.id}`,
                            'Unterschrift angefragt — ein anderes Teammitglied muss in den Pending Reviews freigeben');
                        case 'signed_waiting_info':
                          return chip('border-emerald-500/40 bg-emerald-500/10 text-emerald-300', 'NDA gezeichnet ✓ · wartet auf Unterlagen', `live-nda-signed-${p.id}`,
                            'NDA ist gegengezeichnet — der Broker muss jetzt IM/Unterlagen schicken');
                        case 'info_received':
                          return chip('border-emerald-500/50 bg-emerald-500/15 text-emerald-200', 'Rückmeldung erhalten — prüfen', `live-nda-info-${p.id}`,
                            'Der Broker hat nach dem NDA geantwortet (Unterlagen/IM?) — Mail & Deal prüfen');
                        case 'prior_signed':
                          return chip('border-emerald-500/40 bg-emerald-500/10 text-emerald-300', 'NDA liegt vor ✓ · wartet auf Unterlagen', `live-nda-prior-${p.id}`,
                            p.nda_stage?.at ? `Gezeichnet am ${new Date(p.nda_stage.at).toLocaleDateString('de-DE')}` : 'Laut Mailtext bereits unterzeichnet');
                        case 'comments_sent':
                          return chip('border-amber-500/40 bg-amber-500/10 text-amber-300', 'Anmerkungen gesendet · wartet auf neues NDA', `live-nda-comments-${p.id}`,
                            'NDA-Kommentare wurden an den Broker geschickt — die überarbeitete Version steht aus');
                        case 'revised_received':
                          return (
                            <button onClick={openNda} data-testid={`live-nda-view-btn-${p.id}`}
                              className="text-[10px] border border-violet-500/60 bg-violet-500/10 text-violet-200 hover:bg-violet-500/20 rounded-sm px-2 py-0.5 transition-colors"
                              title="Nach unseren Anmerkungen kam eine neue NDA-Version — prüfen & zeichnen">
                              Neues NDA erhalten — prüfen & zeichnen
                            </button>
                          );
                        case 'nda_received':
                          return (
                            <button onClick={openNda} data-testid={`live-nda-view-btn-${p.id}`}
                              className="text-[10px] border border-violet-500/40 text-violet-300 hover:bg-violet-500/10 rounded-sm px-2 py-0.5 transition-colors">
                              NDA erhalten — ansehen & zeichnen
                            </button>
                          );
                        case 'broker_replied':
                          return chip('border-amber-500/40 bg-amber-500/10 text-amber-300', 'Broker-Antwort erhalten (ohne NDA)', `live-nda-replied-${p.id}`,
                            'Der Broker hat geantwortet, aber kein NDA-Dokument angehängt — Mail prüfen');
                        case 'requested':
                          if (p.nda_reminder_due) return ndaRemindButtons(p, 'live', 'text-[10px] px-2 py-0.5');
                          return chip('border-sky-500/40 bg-sky-500/10 text-sky-300',
                            `NDA angefordert · wartet auf Broker${p.nda_reminder?.sent_at ? ` · erinnert ${fmtDt(p.nda_reminder.sent_at).slice(0, 6)}` : ''}`,
                            `live-nda-requested-${p.id}`);
                        default:
                          return (
                            <button onClick={() => setNdaRequestFor(p)} data-testid={`live-nda-request-btn-${p.id}`}
                              className="text-[10px] border border-violet-500/40 text-violet-300 hover:bg-violet-500/10 rounded-sm px-2 py-0.5 transition-colors">
                              NDA anfordern
                            </button>
                          );
                      }
                    })()}
                    <button onClick={() => { setDupFor(p); setDupOf(''); }} data-testid={`live-duplicate-btn-${p.id}`}
                      title="Doppelt eingereichter Deal? Als Duplikat markieren — Deal geht ohne Broker-Mail auf Passed"
                      className="text-[10px] border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 rounded-sm px-2 py-0.5 transition-colors">
                      Duplikat…
                    </button>
                    <button onClick={() => setDeclineFor(p)} data-testid={`live-decline-btn-${p.id}`}
                      title="Absage-Mail an den Broker (KI-Vorschlag) — Deal geht auf Passed"
                      className="text-[10px] border border-red-500/40 text-red-300 hover:bg-red-500/10 rounded-sm px-2 py-0.5 transition-colors">
                      Absagen…
                    </button>
                    <button onClick={() => navigate(`/projects/${p.created_project_id}`)}
                      className="text-sky-400 hover:underline flex items-center gap-1">
                      Open deal <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {(createdPassed.length + declinedList.length) > 0 && (() => {
        const items = [
          ...createdPassed.map(p => ({ ...p, _kind: 'passed' })),
          ...declinedList.map(p => ({ ...p, _kind: 'declined' })),
        ].sort((a, b) => (b.processed_at || '').localeCompare(a.processed_at || ''));
        return (
          <div className="border border-border/60 rounded-sm bg-card/60">
            <button onClick={() => setPassedOpen(o => !o)} data-testid="passed-deals-toggle"
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left">
              <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${passedOpen ? 'rotate-90' : ''}`} />
              <span className="text-xs text-foreground/80 font-medium">Passed brokered deals</span>
              <span className="text-[10px] font-mono border border-border rounded px-1.5 py-0.5 text-muted-foreground">{items.length}</span>
              <span className="text-[10px] text-muted-foreground ml-auto hidden sm:block">
                {declinedList.length} abgesagt · {createdPassed.length} created & später gepasst
              </span>
            </button>
            {passedOpen && (
              <div className="px-3 pb-2.5 space-y-1" data-testid="passed-deals-list">
                {items.map(p => (
                  <div key={p.id} className="flex items-center gap-2 px-3 py-1.5 border border-border/40 rounded-sm text-xs text-muted-foreground flex-wrap">
                    <span className="text-[9px] font-mono border border-red-500/30 bg-red-500/10 text-red-300/80 rounded px-1 py-0.5 uppercase">
                      {p._kind === 'declined' ? 'abgesagt' : p.project_stage === 'deleted' ? 'gelöscht' : 'passed'}
                    </span>
                    {p.duplicate_marked_at && (
                      <span className="text-[9px] font-mono border border-amber-500/30 bg-amber-500/10 text-amber-300/80 rounded px-1 py-0.5"
                        title={`Als Duplikat markiert${p.duplicate_marked_by ? ` von ${p.duplicate_marked_by}` : ''}`}
                        data-testid={`passed-duplicate-badge-${p.id}`}>
                        Duplikat
                      </span>
                    )}
                    <span className="text-foreground/60">{p.project_name || p.ai?.company_name || p.subject}</span>
                    <span className="hidden sm:inline">· {p.from_email}</span>
                    {p._kind === 'declined' && p.decline_reason && (
                      <span className="text-muted-foreground/70 italic truncate max-w-[300px]" title={p.decline_reason}>„{p.decline_reason}"</span>
                    )}
                    {p.created_project_id && (
                      <button onClick={() => navigate(`/projects/${p.created_project_id}`)}
                        className="text-sky-400/80 hover:underline flex items-center gap-1 ml-auto">
                        Open deal <ExternalLink className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {processed.length > 0 && (
        <div>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Dismissed — kein Deal (Spam, Verteiler etc.)</p>
          <div className="space-y-1" data-testid="processed-proposals">
            {processed.map((p, i) => (
              <div key={p.id} className="flex items-center gap-2 px-3 py-1.5 border border-border/50 rounded-sm text-xs text-muted-foreground flex-wrap">
                <span className={`text-[9px] font-mono border rounded px-1 py-0.5 uppercase ${p.status === 'created' ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : p.status === 'declined' ? 'border-red-500/40 bg-red-500/10 text-red-300' : 'border-border bg-secondary/40'}`}>
                  {p.status === 'declined' ? 'abgesagt' : p.status}
                </span>
                <span className="text-foreground/70">{p.ai?.company_name || p.subject}</span>
                <span>· {p.from_email}</span>
                {p.status === 'declined' && p.decline_reason && (
                  <span className="text-muted-foreground/70 italic truncate max-w-[300px]" title={p.decline_reason}>„{p.decline_reason}"</span>
                )}
                {p.status === 'created' && p.created_project_id && (
                  <button onClick={() => navigate(`/projects/${p.created_project_id}`)}
                    className="text-sky-400 hover:underline flex items-center gap-1 ml-auto">
                    Open deal <ExternalLink className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <BrokerStatsSection />

      <DeclineDialog proposal={declineFor} open={!!declineFor} onClose={() => setDeclineFor(null)}
        onDone={() => { setDeclineFor(null); load(); }} />
      <NdaRequestDialog proposal={ndaRequestFor} open={!!ndaRequestFor} onClose={() => setNdaRequestFor(null)}
        onDone={() => { setNdaRequestFor(null); load(); }} />
      <NdaDialog proposal={ndaFor} open={!!ndaFor} onClose={() => setNdaFor(null)}
        onDone={() => { setNdaFor(null); load(); }} userName={user?.name || ''} />
      <AddMailDialog open={addMailOpen} onClose={() => setAddMailOpen(false)}
        onDone={() => { setAddMailOpen(false); load(); }} />
      <Dialog open={!!dupFor} onOpenChange={(o) => { if (!o) setDupFor(null); }}>
        <DialogContent className="max-w-md bg-card border-border" data-testid="duplicate-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">Duplikat entfernen</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              „{dupFor?.project_name || dupFor?.ai?.company_name || dupFor?.subject}" wird als Duplikat markiert:
              Der Deal geht <b className="text-foreground/80">ohne Broker-Mail</b> auf Passed und verschwindet aus der Live-Liste.
            </p>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Ist Duplikat von (optional)</p>
              <select value={dupOf} onChange={e => setDupOf(e.target.value)} data-testid="duplicate-of-select"
                className="w-full text-sm bg-secondary border border-border text-foreground rounded-sm px-2 h-8 cursor-pointer focus:outline-none">
                <option value="">— nicht zuordnen —</option>
                {createdLive.filter(d => d.id !== dupFor?.id && d.created_project_id).map(d => (
                  <option key={d.id} value={d.created_project_id}>{d.project_name || d.ai?.company_name || d.subject}</option>
                ))}
              </select>
              <p className="text-[10px] text-muted-foreground mt-1">Bei Zuordnung wird der Hinweis am verbleibenden Deal protokolliert.</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setDupFor(null)} data-testid="duplicate-cancel-btn"
                className="rounded-sm text-xs h-8 border-border">Abbrechen</Button>
              <Button size="sm" onClick={markDuplicate} disabled={dupBusy} data-testid="duplicate-confirm-btn"
                className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm text-xs h-8">
                {dupBusy && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                Als Duplikat markieren
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
